import React, { cloneElement, ReactElement, useMemo } from 'react';
import useStorageAccessSignature from '../utils/useStorageAccessSignature';
import {
  getFileContainerFromUrl,
  getFileUrlWithSignature,
} from '../utils/storageAccess';

// Supported url properties of the child element
const URL_PROPS = ['src', 'href', 'source', 'url'] as const;
type UrlProp = typeof URL_PROPS[number];

type ChildProps = {
  [K in UrlProp]?: string;
};

interface Props {
  children: ReactElement<ChildProps>;
}

const getUrlFromProps = (
  props: ChildProps
): { value: string; prop: UrlProp | null } => {
  for (const prop of URL_PROPS) {
    if (props[prop]) {
      return { value: props[prop]!, prop };
    }
  }
  return { value: '', prop: null };
};

/**
 * Signs the url of the child element (src, href, source or url) with a read
 * signature if it points to a private storage container.
 */
const SignedUrl: React.FC<Props> = ({ children }: Props) => {
  const { value: originalUrl, prop: urlProp } = useMemo(
    () => getUrlFromProps(children.props),
    [children.props]
  );

  const containerName = useMemo(() => getFileContainerFromUrl(originalUrl), [
    originalUrl,
  ]);

  const storageAccessSignature = useStorageAccessSignature(containerName);
  const signedUrl = useMemo(
    () => getFileUrlWithSignature(originalUrl, storageAccessSignature.data),
    [storageAccessSignature.data, originalUrl]
  );

  if (!originalUrl || !containerName || !urlProp) {
    return children;
  }

  return !storageAccessSignature.isFetching
    ? cloneElement(children, {
        ...children.props,
        [urlProp]: signedUrl,
      })
    : null;
};

export default SignedUrl;
